import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Folder, FolderOpen, Save } from 'lucide-react';
import AuthEditorPanel from '../AuthEditorPanel';
import VariableKvTable from '../ui/VariableKvTable';
import LexicalRichEditor from '../ui/LexicalRichEditorLazy';
import ScriptsSection from './ScriptsSection';
import { useCollectionPageDraft } from './useCollectionPageDraft';
import { withOwnFolderVariables } from '../../utils/folderVariables';

const KV_INPUT_CLASS = 'request-builder-kv-input w-full rounded-md focus:outline-none';
const KV_INPUT_STYLE = { minHeight: '36px' };

/**
 * The page a collection OR a folder opens as an editor tab (owner D1/D3,
 * 28.09.2026): Docs · Authorization · Variables · Scripts over one shared draft
 * (useCollectionPageDraft). CollectionEditor and FolderEditor are thin wrappers
 * that only supply what differs: the kind, extra `{{chip}}` scopes, and a
 * notice above the sections.
 */
export default function CollectionPage({
  kind = 'collection',
  initialData,
  onDataChange,
  onSave,
  canEdit = true,
  selectedEnvironment = null,
  workspaceVariables = [],
  globalVariables = [],
  currentWorkspaceId = null,
  onVariableUpdate,
  extraInputProps = null,
  notice = null,
}) {
  const { t } = useTranslation();
  const isFolder = kind === 'folder';
  const draft = useCollectionPageDraft({ initialData, onDataChange, onSave, canEdit, onVariableUpdate, kind });
  const { local } = draft;

  const sections = useMemo(
    () => [
      { key: 'docs', label: t('collection.docs') },
      { key: 'authorization', label: t('collection.authorization') },
      { key: 'variables', label: t('collection.variables') },
      { key: 'scripts', label: t('collectionPage.scripts') },
    ],
    [t]
  );

  const variableProps = useMemo(
    () => ({
      selectedEnvironment,
      workspaceVariables,
      globalVariables,
      // On a folder page these are the COLLECTION's rows (extraInputProps);
      // on the collection page they are the draft being edited.
      collectionVariables: draft.variablesForInput,
      onVariableUpdate: draft.handleInlineVariableUpdate,
      variableContext: { collectionId: initialData?.collectionId, workspaceId: currentWorkspaceId },
      ...(extraInputProps || {}),
    }),
    [selectedEnvironment, workspaceVariables, globalVariables, draft.variablesForInput,
      draft.handleInlineVariableUpdate, initialData?.collectionId, currentWorkspaceId, extraInputProps]
  );

  // A folder's auth also sees the folder's own (draft) variables; its
  // Variables table keeps ancestors only.
  const authVariableProps = useMemo(
    () => (isFolder
      ? {
        ...variableProps,
        folderVariables: withOwnFolderVariables(draft.variablesForInput, variableProps.folderVariables, {
          collectionId: initialData?.collectionId,
          folderId: initialData?.folderId,
          folderName: initialData?.name,
        }),
      }
      : variableProps),
    [isFolder, variableProps, draft.variablesForInput, initialData?.collectionId, initialData?.folderId, initialData?.name]
  );

  const HeaderIcon = isFolder ? FolderOpen : Folder;
  return (
    <div className="collection-editor flex flex-col h-full min-h-0 bg-white dark:bg-[#161618]" data-page-kind={kind}>
      <div className="flex-shrink-0 border-b border-gray-200 px-5 py-3 dark:border-zinc-700">
        <div className="editor-header-toolbar editor-header-toolbar--flush">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <HeaderIcon size={18} className="text-amber-600 shrink-0" aria-hidden />
            <h1 className="text-sm font-semibold text-gray-900 truncate dark:text-zinc-100 leading-none">{local.name}</h1>
          </div>
          {canEdit && (
            <button
              type="button"
              onClick={draft.handleSave}
              className="request-header-save inline-flex items-center gap-2 px-3 py-1 rounded-md bg-transparent text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors text-xs font-medium flex-shrink-0"
              aria-label={t(isFolder ? 'collectionPage.saveFolder' : 'collection.saveCollection')}
            >
              <Save size={14} strokeWidth={2} />
              <span>{t('common.save')}</span>
            </button>
          )}
        </div>
      </div>
      <div className="flex-shrink-0 border-b border-gray-200 dark:border-zinc-700" style={{ paddingLeft: 20, paddingRight: 20 }}>
        <nav className="collection-editor-subnav flex space-x-1">
          {sections.map(({ key, label }, i) => (
            <button
              key={key}
              type="button"
              onClick={() => draft.setSection(key)}
              style={{ paddingLeft: i === 0 ? 0 : undefined, paddingRight: 10 }}
              className={`py-0.5 border-b-2 font-medium text-xs rounded bg-transparent transition-colors duration-150 ${
                local.section === key
                  ? 'text-blue-600 border-blue-500'
                  : 'collection-editor-tab-inactive text-gray-500 hover:text-gray-700 hover:border-gray-300 border-transparent'
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>
      {notice}

      <div className="flex-1 min-h-0 overflow-hidden">
        {local.section === 'authorization' && (
          <div className="panel-scroll h-full editor-tab-body">
            {/* variableProps turns every field into the variable-aware editor;
                without it AuthEditorPanel falls back to a plain <input>. */}
            <AuthEditorPanel
              auth={local.auth}
              onChange={draft.updateAuth}
              disabled={!canEdit}
              showInheritOption={isFolder}
              inheritHelp={isFolder ? t('collectionPage.folderInheritHint') : null}
              variableProps={authVariableProps}
            />
          </div>
        )}

        {local.section === 'variables' && (
          <div className="panel-scroll h-full editor-tab-body">
            <VariableKvTable
              variables={local.variables}
              disabled={!canEdit}
              onUpdate={draft.updateVariable}
              onRemove={draft.removeVariable}
              onKeyChange={draft.handleVariableKeyChange}
              inputClassName={KV_INPUT_CLASS}
              inputStyle={KV_INPUT_STYLE}
              menuClassName={isFolder ? 'fv-option-menu' : 'cv-option-menu'}
              inputProps={variableProps}
            />
          </div>
        )}

        {local.section === 'scripts' && (
          <ScriptsSection
            kind={kind}
            preRequestScript={local.preRequestScript}
            testScript={local.testScript}
            onChange={draft.setScript}
            disabled={!canEdit}
          />
        )}

        {local.section === 'docs' && (
          <div className="collection-docs-tab-shell h-full flex flex-col min-h-0 overflow-hidden">
            <LexicalRichEditor
              fallback={<div className="p-4 text-sm text-gray-400">Loading editor…</div>}
              value={local.documentation}
              onChange={draft.setDocumentation}
              disabled={!canEdit}
              placeholder="Write documentation, or press '/' for commands…"
            />
          </div>
        )}
      </div>
    </div>
  );
}
